import {
  Building2,
  Container,
  Plane,
  Ship,
  TrainFront,
  TriangleAlert,
  Truck,
  type LucideIcon,
} from 'lucide-react'

import type { TransportMode } from '@/types/domain'

/* Lucide is the shadcn default set, adopted deliberately (DESIGN.md § icons).
 * The brand guideline has its own icon page (p.19) whose drawing style Lucide
 * does not match; the mismatch is accepted in exchange for full coverage and
 * zero drawing work, and brand icons stay correct for marketing surfaces. */

/** Resolves an API-supplied icon name to a component. The API sends names
 *  rather than components, so this is the one place the mapping lives. */
const BY_NAME: Record<string, LucideIcon> = {
  'building-2': Building2,
  container: Container,
  plane: Plane,
  ship: Ship,
  'train-front': TrainFront,
  'triangle-alert': TriangleAlert,
  truck: Truck,
}

export function iconByName(name: string | undefined): LucideIcon | undefined {
  return name ? BY_NAME[name] : undefined
}

export const MODE_ICONS: Record<TransportMode, LucideIcon> = {
  sea: Ship,
  rail: TrainFront,
  road: Truck,
  air: Plane,
  multimodal: Container,
}

/** The `modes` catalogue key for a transport mode. Translate at the point of
 *  render: `t(modeLabelKey(row.mode))`. A baked-in string here would not follow
 *  a language switch. */
export function modeLabelKey(mode: TransportMode): `modes.${TransportMode}` {
  return `modes.${mode}`
}

/** The wire's directory code ("RAIL", "MULTI" — directory transport_refs) as
 *  the domain mode, or undefined when it is unset or unrecognised.
 *
 *  The server serves the CODE and the client maps it to its own vocabulary
 *  (0003's rule for wire values); this is the one place that mapping lives, so
 *  quotes and cases cannot translate the same code two ways. Undefined rather
 *  than a guess for the unknown case — what to render then is the caller's
 *  question: a quote card drops the mode, a case list falls back to the
 *  container icon. */
const MODE_BY_CODE: Record<string, TransportMode> = {
  sea: 'sea',
  rail: 'rail',
  road: 'road',
  air: 'air',
  multi: 'multimodal',
  multimodal: 'multimodal',
}

export function modeFromCode(code: string | undefined): TransportMode | undefined {
  return code ? MODE_BY_CODE[code.toLowerCase()] : undefined
}
